/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { PromotionsListScreen } from '../../../promotions/public-api';
import { PriceListsListScreen } from '../../../priceLists/public-api';
import { LoyaltyProgramScreen } from '../../../loyalty/public-api';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { getManifestItemsForSurface } from '../navigationManifest';
import { useNavVisibilityContext } from '../useNavVisibilityContext';
import { HubSegmentChips, type HubSegmentChip } from './HubSegmentChips';

type PricingSegment = 'promos' | 'lists' | 'loyalty';

/** Route params accepted by PricingHub for deep-linking to a segment. */
export type PricingHubRouteParams = {
  initialSegment?: PricingSegment;
};

/**
 * Cada segmento hereda el gate de su ítem del manifiesto (stackOnly):
 * flag global + módulo + capability. Si el dueño apaga la función, el chip
 * desaparece; si apaga las tres, la puerta «Promociones y precios» no existe.
 */
const SEGMENT_DEFS: Array<
  HubSegmentChip<PricingSegment> & { childRoute: string }
> = [
  { key: 'promos', label: 'Promociones', testID: 'pricing-hub.segment.promos', childRoute: 'PromotionsList' },
  { key: 'lists', label: 'Listas de precios', testID: 'pricing-hub.segment.lists', childRoute: 'PriceListsList' },
  { key: 'loyalty', label: 'Fidelización', testID: 'pricing-hub.segment.loyalty', childRoute: 'LoyaltyProgram' },
];

/**
 * Hub de Promociones y precios: una puerta en el nav, tabs para las tres
 * palancas. Monta las pantallas existentes sin tocarlas (cada una conserva su
 * header con filtros y acciones propias).
 */
export function PricingHubScreen() {
  const route = useRoute<any>();
  const navCtx = useNavVisibilityContext();

  const visibleChips = useMemo(() => {
    const stackItems = getManifestItemsForSurface('stackOnly', navCtx);
    const visibleRoutes = new Set(stackItems.map((item) => item.route));
    return SEGMENT_DEFS.filter((def) => visibleRoutes.has(def.childRoute));
  }, [navCtx]);

  const initialSegmentParam = route.params?.initialSegment as PricingSegment | undefined;
  const [segment, setSegment] = useState<PricingSegment>(
    initialSegmentParam ?? 'promos',
  );

  useEffect(() => {
    const next = route.params?.initialSegment as PricingSegment | undefined;
    if (next) {
      setSegment(next);
    }
  }, [route.params?.initialSegment]);

  // Fallback cuando el segmento activo dejó de ser visible (owner apagó la
  // función mid-session) o el deep-link pidió un segmento gated.
  const active = visibleChips.some((chip) => chip.key === segment)
    ? segment
    : (visibleChips[0]?.key ?? segment);

  return (
    <View style={styles.root}>
      {visibleChips.length > 1 ? (
        <HubSegmentChips segments={visibleChips} active={active} onChange={setSegment} />
      ) : null}
      <View style={styles.body}>
        {active === 'promos' ? <PromotionsListScreen /> : null}
        {active === 'lists' ? <PriceListsListScreen /> : null}
        {active === 'loyalty' ? <LoyaltyProgramScreen /> : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  body: { flex: 1, minHeight: 0 },
});
